import { IPreventTransientAccessQueryResult } from '../../../prevent_transient_access/queries/prevent_transient_access_query';
import { Section } from '../../sections/builders/section_builder';
import { IQuery } from 'core-ts-types';
import { IRegisterSectionsDatasource } from '../datasources/register_sections_datasource';
import { RegistrationFailureModel } from '../models/registration_failure_model';
import { ITermCoursesDatasource } from '../../term_courses/datasources/term_courses_datasource';
import { SchedulerModel } from '../models/scheduler_model';
import { ScheduledSectionServant } from '../servants/scheduled_section_servant';
import {
  ISection,
  IPusherOutcome,
  ISectionType,
  IPlannedCourse,
  IRawSection
} from '../../../interfaces/scheduler_interfaces';
import { ISectionsDatasource } from '../../sections/datasources/sections_datasource';
import { IDefineCommand, ICommand, IQueryMediator } from 'core-ts-types';
import * as _4 from 'lodash4';
import { ScheduledSectionStatus } from '../types/scheduled_section_status_enum';
import { ITermSectionTypesDatasource } from '../../term_courses/datasources/term_section_types_datasource';
import { RegistrationCallbackServant } from '../servants/registration_callback_servant';
import { IRegistrationActivityDatasource } from '../../registration_transactions/datasources/registration_activity_datasource';
import { RegistrationReceiptServant } from '../servants/registration_receipt_servant';
import { UpdateCalendarSectionTimesCommand } from '../../calendar/commands/update_calendar_section_times_command';
import { RegistrationRecieptCommand } from '../../../registration_receipt/commands/registration_reciept_command';
import { INotificationDelegate } from '../../../global/helpers/scheduler_notification_delegate';
import { RemoveSectionSelectionCommand } from '../../../components/section_selections/commands/remove_section_selection_command';
import { MultiSectionsDropWithdrawRequestStateCommand } from '../../../request_state/commands/multi_sections_drop_withdraw_request_state_command';
import { ScheduledSectionRequestStateCommand } from '../../../request_state/commands/scheduled_section_request_state_command';

export type DropCourseSectionsCommand = (
  termId: IPlannedCourse['term'],
  courseIds: Array<IPlannedCourse['id']>,
  isWaitlist: boolean,
  sessionId: IRawSection['session'],
  section_pins: Record<string, any>,
  sectionIds: Array<Section['id']>,
  student_credentials?: IPreventTransientAccessQueryResult['output']
) => ng.IPromise<IQuery[]>;

// TODO: Loop over all sections and drop each
angular
  .module('capability-ng-scheduler')
  .service(
    'dropCourseSectionsCommand',
    function dropCourseSectionsCommand(
      $http: ng.IHttpService,
      defineCommand: IDefineCommand,
      log: any,
      $q: ng.IQService,
      $timeout: ng.ITimeoutService,
      ngSectionsDatasource: ISectionsDatasource,
      getCourseSchedulerCommand: ICommand,
      ngScheduledSectionServant: ScheduledSectionServant,
      pusherService: any,
      ngSchedulerModel: SchedulerModel,
      ngTermCoursesDatasource: ITermCoursesDatasource,
      QueryMediator: IQueryMediator,
      ngRegistrationAlertsServant: any,
      ngRegistrationActivityDatasource: IRegistrationActivityDatasource,
      ngRegistrationFailureModel: RegistrationFailureModel,
      ngRegisterSectionsDatasource: IRegisterSectionsDatasource,
      ngRegistrationCallbackServant: RegistrationCallbackServant,
      ngRegistrationReceiptServant: RegistrationReceiptServant,
      registrationActivityCommand: ICommand,
      ngTermSectionTypesDatasource: ITermSectionTypesDatasource,
      registrationRecieptCommand: RegistrationRecieptCommand,
      updateCalendarSectionTimesCommand: UpdateCalendarSectionTimesCommand,
      ngSchedulerNotificationDelegate: INotificationDelegate,
      removeSectionSelectionCommand: RemoveSectionSelectionCommand,
      multiSectionsDropWithdrawRequestStateCommand: MultiSectionsDropWithdrawRequestStateCommand,
      scheduledSectionRequestStateCommand: ScheduledSectionRequestStateCommand
    ) {
      'use strict';

      return defineCommand(
        'dropCourseSectionsCommand',
        (
          deferred,
          termId: IPlannedCourse['term'],
          courseIds: Array<IPlannedCourse['id']>,
          isWaitlist: boolean,
          sessionId: IRawSection['session'] = -1,
          section_pins: Record<string, any> = {},
          sectionIds: Array<Section['id']> = [],
          student_credentials?: IPreventTransientAccessQueryResult['output']
        ) => {
          // If no courseIds, drop action is coming from non-pin multi-drop so do NOT do any checks
          const skipChecks = _4.isEmpty(courseIds);

          const statusCheck = (section: ISection) =>
            isWaitlist
              ? ScheduledSectionServant.isSectionWaitListed(section)
              : ngScheduledSectionServant.isSectionRegistered(section);

          const sectionsEligibleForDrop = !skipChecks
            ? _4.filter(
                ngSectionsDatasource.read(),
                (section: ISection) => _4.includes(courseIds, section.course) && statusCheck(section)
              )
            : [];

          if (!skipChecks && sessionId && _4.toString(sessionId) !== '-1') {
            const sectionTypeIdsForSession = _4.compact(
              _4.map(
                _4.filter(
                  ngTermSectionTypesDatasource.read(),
                  (sectionType: ISectionType) => _4.toString(sectionType.session) === _4.toString(sessionId)
                ),
                'id'
              )
            );
            _4.remove(
              sectionsEligibleForDrop,
              (section) => !_4.includes(sectionTypeIdsForSession, section.section_type)
            );
          }

          const sectionIdsToDrop = _4.isEmpty(sectionIds)
            ? _4.map(sectionsEligibleForDrop, (section) => section['id'])
            : sectionIds;

          const sectionStatusesBeforeRegistration = {};
          _4.each(sectionIdsToDrop, (sectionId: number) => {
            const sctn = ngSectionsDatasource.get(sectionId);
            if (sctn) {
              sectionStatusesBeforeRegistration[sectionId] = sctn.status;
            }
          });

          if (_4.isEmpty(sectionIdsToDrop)) {
            multiSectionsDropWithdrawRequestStateCommand(false);
            deferred.reject();
            return;
          }

          const callback_function = (data: any) => {
            registrationActivityCommand(termId).then((d) => {
              const sectionsReceiptData = _4.filter(
                ngRegistrationReceiptServant.getReceiptData(sectionIdsToDrop),
                'isActiveRecord'
              );
              registrationRecieptCommand(sectionsReceiptData, true);
            });
            scheduledSectionRequestStateCommand(sectionIdsToDrop, false);
            ngRegistrationAlertsServant.hidePendingAlert();
            const dropOutcomeData = _4.compact(_4.map(sectionIdsToDrop, (id: number) => data[id]));
            const sectionDropOutcome = _4.partition(
              dropOutcomeData,
              (sectionOutcome: IPusherOutcome) => sectionOutcome.success
            );

            // if success
            _4.each(sectionDropOutcome[0], (sectionOutcome: IPusherOutcome) => {
              const section = ngSectionsDatasource.get(sectionOutcome.id);
              if (section) {
                section.status = ScheduledSectionStatus.UNSCHEDULED;
                ngSectionsDatasource.put(section);
              }

              const removeSectionFromReg = ngRegisterSectionsDatasource.get(sectionOutcome.id);
              if (removeSectionFromReg) {
                ngRegisterSectionsDatasource.del(sectionOutcome.id);
              }

              removeSectionSelectionCommand(sectionOutcome.id);
              // Get and refresh sections
              getCourseSchedulerCommand(termId);
              ngRegistrationCallbackServant.removeSectionError(sectionOutcome.id);
            });

            // if failed
            _4.each(sectionDropOutcome[1], (sectionOutcome: IPusherOutcome) => {
              const section = ngSectionsDatasource.get(sectionOutcome.id);
              if (section) {
                section.status = sectionStatusesBeforeRegistration[sectionOutcome.id];
                ngSectionsDatasource.put(section);
              }
              ngRegistrationFailureModel.put({
                type: isWaitlist ? 'WAITLIST_DROP' : 'DROP',
                id: sectionOutcome.id,
                message: sectionOutcome.message
              });
            });

            // TODO: TECHDEBT
            const RUN_QUERY_DELAY = 350;
            $timeout(() => {
              QueryMediator.runQueries(ngSectionsDatasource);
            }, RUN_QUERY_DELAY);

            updateCalendarSectionTimesCommand();
            multiSectionsDropWithdrawRequestStateCommand(false);
            deferred.resolve(
              QueryMediator.runQueries(ngSectionsDatasource, ngTermCoursesDatasource, ngRegisterSectionsDatasource)
            );
            // Show info alerts for non registered sections
            ngRegistrationAlertsServant.showOtherRequiredSectionsAlert(dropOutcomeData);
          };

          _4.each(sectionIdsToDrop, (id: number) => {
            const section = ngSectionsDatasource.get(id);
            if (section) {
              section.status = ScheduledSectionStatus.PENDING;
              ngSectionsDatasource.put(section);
            }
          });
          QueryMediator.runQueries(ngSectionsDatasource, ngTermCoursesDatasource);
          ngRegistrationAlertsServant.showPendingAlert();
          // Call drop HTTP request

          const SECONDS_60 = 60000;
          const timeout = $q.defer();

          $timeout(() => {
            timeout.resolve();
          }, SECONDS_60);

          $http({
            method: 'POST',
            url: `/reg/register/term/${termId}/`,
            data: {
              method: 'DROP',
              sections: sectionIdsToDrop,
              section_pins,
              ...student_credentials
            },
            cache: false,
            timeout: timeout.promise
          })
            .then(({ data }: ng.IHttpPromiseCallbackArg<any>) => {
              pusherService.bindToPusherEvent(
                ngSchedulerModel.read().channel_name,
                'registration_outcome_drop',
                callback_function
              );
            })
            .catch(({ data }: ng.IHttpPromiseCallbackArg<any>) => {
              log.issue('failed to register courses, please try again');
              if (_4.has(data, 'detail')) {
                ngSchedulerNotificationDelegate.warn(data.detail);
              }
              _4.each(sectionIdsToDrop, (id: number) => {
                const section = ngSectionsDatasource.get(id);
                if (section) {
                  section.status = ScheduledSectionStatus.REGISTERED;
                  ngSectionsDatasource.put(section);
                }
              });
              QueryMediator.runQueries(ngSectionsDatasource, ngTermCoursesDatasource);
              scheduledSectionRequestStateCommand(sectionIdsToDrop, false);
              multiSectionsDropWithdrawRequestStateCommand(false);
              deferred.reject(data);
            });
        }
      );
    }
  );



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/course_scheduler/commands/drop_course_sections_command.ts